import { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CheckCircle, Shield, Zap, Wrench } from 'lucide-react';

const CastIronDescalingPricing = () => {
  useEffect(() => {
    
    const metaTags = [
      { 
        name: 'description', 
        content: 'Professional cast iron descaling services Denver Metro. Milwaukee descaling machine removes scale, rust, and buildup. Mainline descaling $995, Kitchen drain descaling $495. Master plumber owned.' 
      },
      { 
        name: 'keywords', 
        content: 'cast iron descaling Denver, Milwaukee descaling machine, sewer pipe scale removal, kitchen drain descaling, cast iron pipe cleaning Colorado' 
      },
      { 
        property: 'og:title', 
        content: 'Cast Iron Descaling Pricing | Denver Colorado | $495-$995 | Colorado First' 
      },
      { 
        property: 'og:description', 
        content: 'Professional cast iron descaling with Milwaukee machines. Remove scale, rust, and buildup from your pipes. Transparent pricing online.' 
      },
      { 
        property: 'og:type', 
        content: 'website' 
      },
      { 
        property: 'og:url', 
        content: 'https://colorado-plumbing.com/cast-iron-descaling-pricing' 
      },
    ];
    
    metaTags.forEach(tag => {
      let element = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (element) {
        element.setAttribute('content', tag.content);
      } else {
        element = document.createElement('meta');
        if (tag.name) {
          element.setAttribute('name', tag.name);
        } else {
          element.setAttribute('property', tag.property!);
        }
        element.setAttribute('content', tag.content);
        document.head.appendChild(element);
      }
    });

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/cast-iron-descaling-pricing');
      document.head.appendChild(canonicalLink);
    }
  }, []);

  return (
    <div className="min-h-screen bg-neutral-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-48 pb-16 bg-gradient-to-br from-success-primary to-authority-navy">
        <div className="container mx-auto px-4 text-center relative z-10 py-16 md:py-24">
          <div className="inline-block px-6 py-3 mb-8 rounded-full bg-action-primary/90 text-white font-bold text-lg">
            ⚡ Milwaukee Descaling Machine • Professional Scale Removal • Chemical-Free Process
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-neutral-white leading-tight">
            Cast Iron Descaling Pricing
            <span className="block text-2xl md:text-3xl text-success-light font-semibold mt-2">
              Restore Your Pipes with Milwaukee Descaling Technology
            </span>
          </h1>
          
          <p className="text-lg text-trust-light/90 mb-8">
            Master Plumber Owned • 7 Years Denver Metro • 293+ Reviews • Milwaukee Descaling Machine Specialists
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            
            <a 
              href="#descaling-pricing-details" 
              className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
            >
              💰 See All Pricing Below
            </a>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="descaling-pricing-details" className="py-20 bg-neutral-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-authority-navy">
              Transparent Cast Iron Descaling Pricing
            </h2>
            <p className="text-xl text-neutral-gray mb-4">
              See actual descaling costs with no hidden fees or surprise charges
            </p>
            <div className="inline-flex items-center bg-success-light px-6 py-3 rounded-full border-2 border-success-primary/20">
              <Shield className="w-6 h-6 text-success-primary mr-3" />
              <span className="text-success-primary font-bold">2 Descaling Services • Milwaukee Equipment • All Pricing Online</span>
            </div>
          </div>

          {/* Sewer Line Descaling */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-trust-light to-success-light/30 rounded-2xl p-8 border-2 border-trust-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Zap size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Sewer Line Descaling - 50 Feet</h3>
                  <p className="text-lg text-neutral-gray">Restore your sewer line's flow with our Milwaukee Descaling Mainline service</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-trust-primary to-trust-deep text-white p-4">
                      <h4 className="text-xl font-bold text-center">Sewer Line Descaling Pricing</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Milwaukee Descaling Mainline - 50 Feet Cast Iron</span>
                        <span className="text-3xl font-bold text-success-primary">$995</span>
                      </div>
                      <div className="bg-action-urgent/10 p-4 rounded-lg border border-action-urgent/20">
                        <p className="text-sm text-action-urgent font-semibold mb-2">⚠️ Important Service Notes:</p>
                        <ul className="text-sm text-authority-navy space-y-1">
                          <li>• Cleanout access only</li>
                          <li>• Does not include jetting (may be needed for debris removal)</li>
                          <li>• Does not include liner installation</li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Service Includes</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Milwaukee Descale machine operation
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Mechanical scale & rust removal
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Buildup removal from mainline
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Chemical-free process
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Extends pipe life significantly
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Smooth drainage restoration
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/services/sewer-services/descale-cast-iron-littleton.png"
                        alt="Cast iron descaling service using Milwaukee machine in Littleton Colorado by Colorado First Plumbing"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Cast Iron Descaling Service - Littleton
                        </h4>
                        <p className="text-sm text-trust-light">
                          Professional Milwaukee descaling machine removing scale and rust from cast iron pipes
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Kitchen Drain Line Descaling */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-success-light to-action-warm/10 rounded-2xl p-8 border-2 border-success-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Wrench size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Kitchen Drain Line Descaling - Up to 50 Feet</h3>
                  <p className="text-lg text-neutral-gray">Keep your kitchen drain flowing smoothly with specialized grease and scale removal</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-success-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-success-primary to-success-eco text-white p-4">
                      <h4 className="text-xl font-bold text-center">Kitchen Drain Descaling Pricing</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Kitchen Drain Line Descaling - Up to 50 Feet</span>
                        <span className="text-3xl font-bold text-success-primary">$495</span>
                      </div>
                      <div className="bg-action-urgent/10 p-4 rounded-lg border border-action-urgent/20">
                        <p className="text-sm text-action-urgent font-semibold mb-2">⚠️ Important Service Notes:</p>
                        <ul className="text-sm text-authority-navy space-y-1">
                          <li>• Does not include jetting</li>
                          <li>• Cleanout access needed</li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Kitchen Descaling Includes</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Milwaukee Descale machine operation
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Grease removal from drain line
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Scale and buildup removal
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Clear pipes for smooth drainage
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Chemical-free cleaning process
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Future clog prevention
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-success-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/services/sewer-services/descale-machine-roots-denver.JPEG"
                        alt="Professional Milwaukee descaling machine used by best sewer company Colorado First Plumbing"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Milwaukee Descaling Machine - Professional Equipment
                        </h4>
                        <p className="text-sm text-trust-light">
                          Advanced descaling technology for superior grease and scale removal from kitchen drains
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Service Disclaimer */}
          <div className="mb-16">
            <div className="bg-neutral-light border border-neutral-border rounded-xl p-6">
              <h3 className="text-xl font-bold text-authority-navy mb-4">💡 Service Information & Pricing Notes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-neutral-gray">
                <div>
                  <h4 className="font-semibold text-authority-navy mb-2">What's Included in Standard Pricing:</h4>
                  <ul className="space-y-1">
                    <li>• Standard parts and installation</li>
                    <li>• Milwaukee descaling machine operation</li>
                    <li>• Professional service and cleanup</li>
                    <li>• Post-service drainage testing</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-authority-navy mb-2">Pricing Variables:</h4>
                  <ul className="space-y-1">
                    <li>• Rate may vary based on specific job variables</li>
                    <li>• Specific brand of parts if needed</li>
                    <li>• Access difficulties or complications</li>
                    <li>• Contact us if you have any questions!</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 text-white shadow-2xl border border-trust-primary/20">
              <h3 className="text-3xl font-bold mb-4">Ready for Professional Cast Iron Descaling?</h3>
              <p className="text-xl mb-6 text-trust-light">
                See your exact price above • No hidden fees • Master plumber owned • Milwaukee equipment specialists
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="/sewer-cleaning-pricing" 
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto"
                >
                  🔧 See All Sewer Cleaning Services
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CastIronDescalingPricing;